import * as Rx from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { ShoppingDelivery } from 'v2/interfaces/shopping';

import apiService from '../api';
import type { ApiService } from '../api';
import tableService from '../table';
import type { TableService } from '../table';

class ShoppingDeliveriesService {
  private list$ = new Rx.BehaviorSubject<ShoppingDelivery[]>([]);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private openQuickview$ = new Rx.BehaviorSubject<boolean>(false);
  private current$ = new Rx.BehaviorSubject<ShoppingDelivery>(null);

  constructor(
    private apiService: ApiService,
    private tableService: TableService
  ) {}

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams) {
    this.paginationParams$.next(pagination);
  }
  public setCurrent = (subscription: ShoppingDelivery): void => this.current$.next(subscription);
  public getCurrent = (): Rx.Observable<ShoppingDelivery> => this.current$;

  public getPagination = (): Rx.Observable<IPaginationParams> => this.paginationParams$;
  public getQuickview = (): Rx.Observable<boolean> => this.openQuickview$;

  public openQuickview = (delivery: ShoppingDelivery): void => {
    this.setCurrent(delivery);
    this.openQuickview$.next(true);
  };

  public closeQuickview = (): void => {
    this.setCurrent(null);
    this.openQuickview$.next(false);
  };

  public list(): Rx.Observable<ShoppingDelivery[]> {
    return this.list$;
  }

  public loadDeliveries() {
    return this.getPagination().pipe(
      tap(() => this.tableService.changeLoading(true)),
      debounceTime(10),
      switchMap(pagination => this.apiService.get('/shopping', pagination)),
      tap(response => this.list$.next(response.data)),
      tap(response => this.paginationResponse$.next(response.pagination)),
      tap(() => this.tableService.changeLoading(false)),
      catchError(() => {
        this.tableService.changeLoading(false);
        this.list$.next([]);
        this.paginationResponse$.next(null);
        return Rx.of([]);
      })
    );
  }

  public loadFiscalDocuments = (invoiceId: string) => {
    return this.apiService.get(`/shopping/${invoiceId}/fiscaldocuments`);
  };

  public producerContact = (invoiceId: string) => {
    return this.apiService.get(`/shopping/${invoiceId}/contactproducer`);
  };

  public getProductReviewCollector = (productId: number) => {
    return this.apiService
      .get(`/products/${productId}/review-collector`)
      .pipe(map(response => response.data))
      .toPromise();
  };

  public getCcbTerm = (invoiceId: string) => {
    return this.apiService.download('GET', `/shopping/${invoiceId}/ccb-term`, null, {}, false, false, 'blob');
  };
}

const shoppingProductService = new ShoppingDeliveriesService(apiService, tableService);
export default shoppingProductService;
